// Curated, offline suggestion engine for ABAY360 smart search. Produces
// Google/Airbnb-style live suggestions grouped by category. Purely additive —
// combined at render time with live property matches + recent searches.

export const LOCATIONS = [
  "Bole", "Kazanchis", "CMC", "Ayat", "Sarbet", "Summit", "Gerji", "Lebu",
  "Old Airport", "Megenagna", "Piassa", "Sululta", "Legetafo", "Hawassa",
  "Adama", "Bahir Dar", "Mekelle", "Gondar", "Dire Dawa", "Jimma",
];

export const TOPICS = [
  "Property investment", "House valuation", "Rental yield", "Mortgage basics",
  "Price per square meter", "First-time buyer guide", "Best areas to invest",
];

export const POPULAR_SEARCHES = [
  "Homes in Bole",
  "Apartments in CMC",
  "Land in Sululta",
  "Luxury villas",
  "Commercial buildings",
  "Investment properties",
];

// Premium search: trending + place-of-interest categories.
export const TRENDING_SEARCHES = [
  "3-bedroom in Bole",
  "Apartments near Edna Mall",
  "Land in Legetafo",
  "High-ROI investment areas",
  "Homes near international schools",
];

export const LANDMARKS = [
  "Edna Mall", "Friendship Center", "Bole Airport", "Meskel Square",
  "Unity Park", "Millennium Hall", "Century Mall", "Mexico Square",
];

export const SCHOOLS = [
  "International Community School", "Sandford International School",
  "German Embassy School", "Bingham Academy", "Flipper International School",
];

export const HOSPITALS = [
  "St. Paul's Hospital", "Black Lion Hospital", "Nordic Medical Centre",
  "Hayat Hospital", "Myungsung Christian (MCM) Hospital",
];

const PROPERTY_TERMS = [
  { key: "house", label: "House" },
  { key: "home", label: "Home" },
  { key: "apartment", label: "Apartment" },
  { key: "condo", label: "Condominium" },
  { key: "condominium", label: "Condominium" },
  { key: "villa", label: "Villa" },
  { key: "land", label: "Land" },
  { key: "commercial", label: "Commercial property" },
  { key: "office", label: "Office space" },
  { key: "shop", label: "Shop" },
  { key: "studio", label: "Studio apartment" },
];

// Build categorized suggestions for a typed query. Returns an array of
// { key, title, items: [{ type, icon, label, value }] }.
export function buildQuerySuggestions(query) {
  const q = (query || "").trim();
  const ql = q.toLowerCase();
  if (!ql) return [];
  const groups = [];

  // --- Locations (+ templated location refinements) ---
  const locItems = [];
  const primary = LOCATIONS.find((l) => l.toLowerCase().startsWith(ql));
  const seen = new Set();
  LOCATIONS.filter((l) => l.toLowerCase().includes(ql))
    .slice(0, 4)
    .forEach((loc) => {
      if (seen.has(loc.toLowerCase())) return;
      seen.add(loc.toLowerCase());
      locItems.push({ type: "location", icon: "location", label: loc, value: loc });
    });
  if (primary && ql.length >= 2) {
    ["Homes", "Apartments", "Commercial", "Land", "Luxury Villas"].forEach((s) => {
      const label = `${primary} ${s}`;
      if (!seen.has(label.toLowerCase())) {
        seen.add(label.toLowerCase());
        locItems.push({ type: "location", icon: "home", label, value: label });
      }
    });
  }
  if (locItems.length) groups.push({ key: "locations", title: "Locations", items: locItems.slice(0, 6) });

  // --- Property term templates ---
  const term = PROPERTY_TERMS.find((t) => t.key.startsWith(ql) || ql.startsWith(t.key));
  if (term && ql.length >= 2) {
    const base = term.label;
    groups.push({
      key: "properties",
      title: "Suggestions",
      items: [
        { type: "property", icon: "home", label: base, value: base },
        { type: "property", icon: "home", label: `${base} for Sale`, value: `${base} for Sale` },
        { type: "property", icon: "home", label: `${base} for Rent`, value: `${base} for Rent` },
        { type: "property", icon: "home", label: `${base} with Garden`, value: `${base} with Garden` },
        { type: "property", icon: "budget", label: `${base} under ETB 20M`, value: `${base} under ETB 20M` },
      ],
    });
  }

  // --- Investment topics ---
  const topics = TOPICS.filter((t) => t.toLowerCase().includes(ql))
    .slice(0, 3)
    .map((t) => ({ type: "topic", icon: "invest", label: t, value: t }));
  if (topics.length) groups.push({ key: "topics", title: "Investment Topics", items: topics });

  // --- Places of interest: landmarks, schools, hospitals ---
  const poi = [];
  LANDMARKS.filter((l) => l.toLowerCase().includes(ql)).slice(0, 2).forEach((l) =>
    poi.push({ type: "landmark", icon: "landmark", label: `Near ${l}`, value: `Near ${l}` }));
  SCHOOLS.filter((s) => s.toLowerCase().includes(ql)).slice(0, 2).forEach((s) =>
    poi.push({ type: "school", icon: "school", label: `Near ${s}`, value: `Near ${s}` }));
  HOSPITALS.filter((h) => h.toLowerCase().includes(ql)).slice(0, 2).forEach((h) =>
    poi.push({ type: "hospital", icon: "hospital", label: `Near ${h}`, value: `Near ${h}` }));
  if (poi.length) groups.push({ key: "poi", title: "Near Landmarks & Amenities", items: poi.slice(0, 4) });

  return groups;
}
